import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRiskConfigCoverage } from '@Actions';
import { Category, Component, ComponentType } from '@Types';
import { NamespacedStoreState } from '@Types/store.types';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { selectCoverageForComponent } from '@Selectors/RiskCoverage.selectors';
import {
	selectTemplateLabel,
	selectUnrecognisedValuesForComponent,
} from '@Selectors/RiskTemplateApply.selectors';
import { selectMissingSourceKind } from '@Selectors/ComponentSource.selectors';
import { isCoverageEligible } from '@Components/Category/componentCoverage';

const NO_UNRECOGNISED_VALUES: string[] = [];

interface Params {
	category?: Category;
	component: Component;
	forLoF: boolean;
	parameterNumber?: number;
	type: ComponentType;
	componentChanged: boolean;
	configId: string;
}

// Everything the Score Setup screen needs from the risk-templates work:
// coverage, unmatched template values, and whether this component's saved
// source is missing from the tenant's data (and is being reconnected). Kept out of
// AddEditComponent so that component's own complexity stays at baseline.
export const useScoreSetupGuidance = ({
	category,
	component,
	forLoF,
	parameterNumber,
	type,
	componentChanged,
	configId,
}: Params) => {
	const dispatch = useDispatch();
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	// Coverage and guidance are per-component, not per-parameter, so only the
	// top-level component screen shows them. Multi-param components (parent
	// and sub-screens) are not supported by risk templates, so they get none
	// (and trigger no coverage fetch).
	const showScoreGuidance =
		isRiskTemplatesEnabled &&
		parameterNumber === undefined &&
		type !== ComponentType.MultiParam;
	const coverageEligible = isCoverageEligible(type);

	const savedCoverageEntry = useSelector((state: NamespacedStoreState) =>
		selectCoverageForComponent(state, category?.name ?? '', component.name),
	);

	// Flag gate lives outside useSelector so the flag-off path never depends on
	// the selector result. A saved component whose source isn't among its
	// picker's options (e.g. a template layer the tenant doesn't have) gets an
	// editable source picker. Derived from the saved component and the loaded
	// option lists, so it survives a page refresh, and locks again once a real
	// source is saved. Multi-param sub-screens have no reconnect support.
	const missingSourceKind = useSelector(selectMissingSourceKind);
	const isFlaggedUnavailable =
		isRiskTemplatesEnabled &&
		!!category &&
		parameterNumber === undefined &&
		typeof missingSourceKind === 'function' &&
		missingSourceKind(forLoF, !!category.isCondition, component) !==
			undefined;

	// A flagged component the user has actually edited. Gates the metadata
	// fetch/apply paths so merely opening a flagged component (mount) never
	// clears its saved unit metadata or resets its score rows.
	const isReconnecting = isFlaggedUnavailable && componentChanged;

	// Coverage describes the *saved* source, so it would misreport the new one
	// the user just picked. Withhold it (pill and footer) until saved.
	const coverageEntry = isReconnecting ? undefined : savedCoverageEntry;

	// Unmatched template values only apply to the top-level component screen;
	// gated outside useSelector with a stable empty array so the selector's
	// result reference doesn't change on every store update.
	const rawUnrecognisedValues = useSelector((state: NamespacedStoreState) =>
		selectUnrecognisedValuesForComponent(
			state,
			forLoF ? 'lof' : 'cof',
			component.name,
		),
	);
	const unrecognisedValues = showScoreGuidance
		? rawUnrecognisedValues
		: NO_UNRECOGNISED_VALUES;
	const templateLabel = useSelector(selectTemplateLabel);

	useEffect(() => {
		if (showScoreGuidance && configId) {
			dispatch(getRiskConfigCoverage(configId));
		}
	}, [showScoreGuidance, configId, dispatch]);

	return {
		showScoreGuidance,
		coverageEligible,
		coverageEntry,
		isFlaggedUnavailable,
		isReconnecting,
		unrecognisedValues,
		templateLabel,
	};
};
